import { takeLatest, call, put, select } from 'redux-saga/effects';
import { getFirebase } from 'react-redux-firebase';
import { push } from 'connected-react-router';
import { sha256 } from 'js-sha256';
import { maybeContentfulReviewStatus, post } from 'utils/api';
import client from 'utils/contentfulService';
import { getCookie } from 'utils/cookieStore';
import Config from 'utils/getEnvConfig';
import MixPanel from 'utils/mixpanelService';
import { isBot } from 'utils/stringUtils';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _flatten from 'lodash/flatten';
import { storeItemForSignIn, setAuthModalState } from 'containers/Auth/actions';
import { makeSelectProfile } from 'containers/Auth/selectors';
import {
  getContentfulRatingQuery,
  getContentfulTopicsQuery,
  getTopicsIdQuery,
} from 'containers/Main/utils';
import {
  GET_ASSESSMENT,
  GET_ANSWERS,
  PUT_ASSESSMENT,
  GET_ASSESSMENT_RESULT,
  GET_ASSESSMENT_REPORT,
} from './constants';
import {
  getAssessmentSuccess,
  getAssessmentFail,
  getAnswersSuccess,
  getAnswersFail,
  putAssessmentResult,
  getAssessmentResultSuccess,
  getAssessmentResultFail,
  processing,
  answersProcessing,
  getAssessmentReportResult,
} from './actions';

const {
  LAMBDA: { ASSESSMENT },
} = Config;

const assessmentHeader = {
  headers: {
    'x-api-key': ASSESSMENT.API_KEY,
  },
};
function* getAssessmentSaga({ payload }) {
  try {
    const slug = payload;
    yield put(processing(true));
    const contentfulRatingQuery = yield call(getContentfulRatingQuery);
    const contentfulTopicsQuery = yield call(getContentfulTopicsQuery, false);
    const response = yield call(() =>
      client.getEntries({
        content_type: 'assessments',
        'fields.slug': slug,
        include: 2,
        ...maybeContentfulReviewStatus,
        ...contentfulRatingQuery,
        ...contentfulTopicsQuery,
      }),
    );
    yield put(getAssessmentSuccess(response));
  } catch (error) {
    yield put(getAssessmentFail(error));
  } finally {
    yield put(processing(false));
  }
}

function* getAnswersSaga({ payload }) {
  try {
    yield put(answersProcessing(true));
    const { slug } = payload;
    let userID;
    let collection;
    try {
      userID = yield getFirebase().auth().currentUser.uid;
      collection = 'user_resources';
    } catch (e) {
      userID = getCookie('userIdTemp');
      collection = 'anonymous_assessments';
    }

    const takenAssessmentDocs = yield getFirebase()
      .firestore()
      .collection(collection)
      .doc(userID)
      .collection('taken_assessments')
      .where('assessment.slug', '==', slug)
      .orderBy('timestamp', 'desc')
      .limit(1)
      .get();
    const takenAssessment = takenAssessmentDocs.docs.map(doc => doc.data())[0];

    if (takenAssessment.result) yield put(getAnswersSuccess(takenAssessment));
    else yield put(getAnswersFail());
  } catch (error) {
    yield put(getAnswersFail(error));
  } finally {
    yield put(answersProcessing(false));
  }
}

function* getAssessmentResultSaga({ payload }) {
  try {
    yield put(processing(true));
    if (isBot()) throw new Error('Do not call Algolia for Bot requests');
    const { answers, slug } = payload;
    const scorings = yield call(
      post,
      ASSESSMENT.URL,
      {
        slug,
        answers,
      },
      assessmentHeader,
    );
    const { resultLinks } = scorings.data;

    const ids = [];
    const overallLink = _get(resultLinks, 'overall', null);
    const scoringLinks = _get(resultLinks, 'scoring', []);
    if (!_isEmpty(overallLink)) ids.push(overallLink);
    if (!_isEmpty(scoringLinks)) ids.push(...scoringLinks);
    // if (typeof payload === 'string') ids.push(payload);
    const response = yield call(() =>
      client.getEntries({
        content_type: 'assessmentResults',
        'sys.id[in]': ids.join(','),
        include: 2,
      }),
    );

    const excludedTopicIds = yield call(getTopicsIdQuery);
    const relatedTopics = _flatten(
      response.items.map(el => el.fields.topics),
    ).filter(
      el =>
        el != null &&
        el.fields.reviewStatus === 'Accepted' &&
        !excludedTopicIds.includes(el.sys.id),
    );

    const sortedItems = ids.map(id => {
      const item = response.items.find(el => el.sys.id === id);
      if (item && item.fields.topics) {
        item.fields.topics = item.fields.topics
          .map(topic => {
            const topicObj = relatedTopics.find(
              el => el.fields.slug === topic.fields.slug,
            );

            if (topicObj) return topic;
            return null;
          })
          .filter(topic => topic !== null);
      }
      return item;
    });

    response.items = sortedItems;

    yield put(
      getAssessmentResultSuccess({
        scoringsObject: scorings.data,
        results: response,
      }),
    );
  } catch (error) {
    yield put(getAssessmentResultFail(error));
  } finally {
    yield put(processing(false));
  }
}

function* putAssessmentSaga({ payload }) {
  try {
    yield put(processing(true));
    yield put(storeItemForSignIn({}));
    const { isAnonymous } = payload;
    let userID;
    if (isAnonymous) {
      userID = getCookie('userIdTemp');
    } else {
      try {
        userID = yield getFirebase().auth().currentUser.uid;
      } catch (error) {
        yield put(storeItemForSignIn({ type: 'assessmentResult', payload }));
        yield put(putAssessmentResult({ error: true }));
        yield put(
          setAuthModalState({
            show: true,
            type: 'save',
          }),
        );
        yield put(processing(false));
      }
    }
    if (userID != null) {
      const scorings = yield call(
        post,
        ASSESSMENT.URL,
        {
          slug: payload.assessment.slug,
          answers: payload.result,
        },
        assessmentHeader,
      );
      const {
        resultLinks: resultID,
        overallScoring,
        scoring,
        overallLabel,
        labels,
        contextData: context,
      } = scorings.data;

      const collection = isAnonymous
        ? 'anonymous_assessments'
        : 'user_resources';
      const { location, ...finalPayload } = payload;
      const { retake, ...data } = finalPayload;
      const {
        assessment, // both objects
        cobrand, // only report
        dob, // both objects
        industry, // only report
        gender, // both objects
        genderOther, // only report
        result, // both objects
        // overallScoring, // both objects
        // scoring, // both objects
        // overallLabel, // both objects
        // labels, // both objects
        // context, // only report
        timestamp, // both objects
      } = data;
      const hashedId = sha256(sha256(sha256(sha256(userID))));

      if (!isAnonymous) {
        try {
          const profile = yield select(makeSelectProfile());

          const profileData = {};
          const mixpanelProfileData = {};
          if (!profile.dob) {
            profileData.dob = dob;
            mixpanelProfileData.age = dob;
          }
          if (!profile.gender) {
            profileData.gender = gender;
            mixpanelProfileData.gender = gender;
          }
          if (
            industry &&
            (!profile.industry || profile.industry !== industry)
          ) {
            profileData.industry = industry;
            mixpanelProfileData.industry = industry;
          }

          if (!_isEmpty(profileData)) {
            yield getFirebase().updateProfile(profileData);
            MixPanel.track('Update account - Successful');
            MixPanel.people.set(mixpanelProfileData);
          }
          // eslint-disable-next-line no-empty
        } catch (e) {}
      }
      // Split data for assessment_reports and user_resources/{user_id}/taken_assessments
      const reportData = {
        id: hashedId,
        assessment,
        cobrand,
        dob,
        gender,
        genderOther,
        industry,
        isAnonymous,
        result,
        context,
        timestamp,
        overallScoring,
        scoring,
        overallLabel,
        labels,
      };
      const takenAssessmentData = {
        assessment,
        dob,
        gender,
        timestamp,
        overallScoring,
        scoring,
        overallLabel,
        labels,
        result,
        resultID,
      };
      // Begin batch
      const reportDoc = yield getFirebase()
        .firestore()
        .collection('assessment_reports')
        .doc();
      const takenAssessmentDoc = yield getFirebase()
        .firestore()
        .collection(collection)
        .doc(userID)
        .collection('taken_assessments')
        .doc();
      takenAssessmentData.report = reportDoc.id;
      const batch = getFirebase()
        .firestore()
        .batch();
      batch.set(reportDoc, reportData);
      batch.set(takenAssessmentDoc, takenAssessmentData);
      yield batch.commit();
      // End of batch

      // yield put(getAssessmentResult(data.resultID));
      yield put(push(`/assessments/${payload.assessment.slug}/results`));
    } else {
      yield put(processing(false));
    }
  } catch (error) {
    yield put(processing(false));
    yield put(putAssessmentResult({ error: true, errorMessage: error }));
  }
}

function* getAssessmentReportSaga({ payload }) {
  try {
    yield put(processing(true));
    const profile = yield select(makeSelectProfile());
    if (profile.role === 'admin') {
      const reportDoc = yield getFirebase()
        .firestore()
        .collection('assessment_reports')
        .doc(payload)
        .get();
      const data = reportDoc.data();
      yield put(getAssessmentReportResult(data));
      yield put(processing(false));
    } else {
      yield put(processing(false));
    }
  } catch (error) {
    yield put(processing(false));
  }
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(GET_ASSESSMENT, getAssessmentSaga);
  yield takeLatest(GET_ANSWERS, getAnswersSaga);
  yield takeLatest(GET_ASSESSMENT_RESULT, getAssessmentResultSaga);
  yield takeLatest(PUT_ASSESSMENT, putAssessmentSaga);
  yield takeLatest(GET_ASSESSMENT_REPORT, getAssessmentReportSaga);
}
